import { Suspense, useContext, useMemo, useState } from "react";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import { TableFilters } from "../../../common/components/ui/table/TableFilters";
import { AuthContext } from "../../../common/components/auth/authContext/AuthContext";
import { useGetInventories } from "../hooks/useGetInventories";
import {
  IInventory,
  INVENTORY_SITUATION,
  INVENTORY_STATUS_LABELS,
} from "@/types/inventory.types";
import { ImageCapture } from "./modals/ImageCapture";
import { convertedImageToTextPost } from "../../../common/services/vehicles.api";
import { useCompanyStore } from "@/common/states/store/Company";
import {
  createInventoryVehicles,
  updateInventory,
} from "@/common/services/inventory-vehicles.api";
import { format } from "date-fns";

import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useAppStore } from "@/common/states/store/slices";
import { ModalChangeStatus } from "./modals/ModalChangeStatus";

function formatInventoryList(vehicles: IInventory[]) {
  return vehicles.map((vehicle) => {
    return {
      ...vehicle,
      date: format(vehicle.date, "dd/MM/yyyy"),
      situation: INVENTORY_STATUS_LABELS[vehicle.situation],
    };
  });
}

const headers = {
  city: "Cidade",
  date: "Data",
  situation: "Situação",
  total: "Total",
};

export const TableInventoryVehicles = () => {
  useContext(AuthContext);

  const [page, setPage] = useState<number>(1);
  const [failedImages, setFailedImages] = useState<
    { base64: string; fileName: string }[]
  >([]);
  const [inventoryToUpdate, setInventoryToUpdate] = useState<IInventory | null>(
    null
  );
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>({
    eventName: "",
    data: undefined,
  });
  const isAction = (eventName: string) => action.eventName === eventName;

  const {
    inventories: {
      closeImageCaptureModal,
      openImageCaptureModal,
      openChangeStatusModal,
      setSucessProcessImages,
    },
  } = useAppStore();
  const {
    inventories: { isConvertingImagesToText, setIsConvertingImagesToText },
  } = useAppStore();
  const {
    inventories: { licensePlates, setLicensePlates },
  } = useAppStore();

  const { notifySuccess, notifyError } = useToastContext();

  const {
    inventories: { filters, setFilters },
  } = useAppStore();

  const { filterMode, selectedCompanies } = useCompanyStore();
  const singleSelectedCompany = useMemo(() => {
    if (filterMode === "cities" && selectedCompanies.length === 1) {
      return selectedCompanies[0];
    }
    return null;
  }, [filterMode, selectedCompanies]);

  const {
    data: inventoriesData,
    isLoading,
    totalItems,
    refetch: refetchInventories,
  } = useGetInventories();

  const handleNewItemClick = () => {
    openImageCaptureModal();
  };

  const handleModalChangeStatus = (data) => {
    setAction({ eventName: "changeSituation", data: data });
    openChangeStatusModal();
  };

  const actions = [
    {
      label: "Atualizar inventário",
      action: (inventory) => {
        setInventoryToUpdate(inventory);
        openImageCaptureModal();
      },
      permission: "edit:inventory",
    },
    {
      label: "Verificar divergência",
      action: (data) => handleModalChangeStatus(data),
      disabled: (data) =>
        [INVENTORY_SITUATION.DIVERGENTE].includes(
          data.situation as INVENTORY_SITUATION
        ),
      permission: "edit:inventory",
    },
  ];

  const processListConvertedImageToText = async (
    listBase64: { base64: string; fileName: string }[]
  ) => {
    const listLicensePlates: { licensePlate: string }[] = [];
    for (const file of listBase64) {
      try {
        const licensePlateResp = await convertedImageToTextPost(file.base64);

        if (!licensePlateResp.ok) {
          notifyError(
            `Não foi possível extrair a placa da imagem: ${file.fileName}`
          );
          setFailedImages((prev) => [
            ...prev,
            { base64: file.base64, fileName: file.fileName },
          ]);
          continue;
        }
        listLicensePlates.push({ licensePlate: licensePlateResp.data });
      } catch (error) {
        console.error("Erro ao processar imagem:", error);
        notifyError(
          error instanceof Error
            ? error.message
            : "Erro desconhecido ao processar imagem"
        );
      }
    }

    return listLicensePlates;
  };

  const handleImageReady = async (
    listBase64: { base64: string; fileName: string }[]
  ) => {
    setIsConvertingImagesToText(true);
    try {
      const plates: { licensePlate: string }[] =
        await processListConvertedImageToText(listBase64);
      setLicensePlates(plates);
    } catch (error) {
      console.error("Erro ao processar imagens para inventário:", error);
      notifyError("Erro ao processar imagens para inventário.");
    } finally {
      setIsConvertingImagesToText(false);
      setSucessProcessImages(true);
    }
  };

  const submitInventory = async (inventoryStatus: INVENTORY_SITUATION) => {
    if (!singleSelectedCompany) {
      notifyError(
        "Por favor, selecione uma única cidade para criar um inventário."
      );
      return;
    }
    try {
      const response = await createInventoryVehicles({
        city: singleSelectedCompany.companyName?.split("/")[0],
        date: new Date(),
        total: licensePlates?.length,
        vehicles: licensePlates,
        situation: inventoryStatus,
        status: inventoryStatus,
      });

      if (response.ok) {
        notifySuccess("Inventário criado com sucesso!");
        refetchInventories();
      } else {
        notifyError(response.message || "Falha ao criar inventário.");
      }
    } catch (error) {
      console.error("Erro ao criar inventário:", error);
      notifyError("Erro inesperado ao criar inventário.");
    } finally {
      closeImageCaptureModal();
      setLicensePlates([]);

      setSucessProcessImages(false);
    }
  };

  const updateExistingInventory = async (
    inventoryStatus: INVENTORY_SITUATION
  ) => {
    if (!inventoryToUpdate) return;

    try {
      const response = await updateInventory(String(inventoryToUpdate._id), {
        total: inventoryToUpdate.total + licensePlates.length,
        vehicles: licensePlates,
        situation: inventoryStatus,
      });

      if (response.ok) {
        notifySuccess("Inventário atualizado com sucesso!");
        refetchInventories();
      } else {
        notifyError(response.message || "Falha ao atualizar inventário.");
      }
    } catch (error) {
      console.error("Erro ao atualizar inventário:", error);
      notifyError("Erro inesperado ao atualizar inventário.");
    } finally {
      closeImageCaptureModal();
      setLicensePlates([]);
      setInventoryToUpdate(null);
      setSucessProcessImages(false);
    }
  };

  const handleSubmitInventory = async (status: INVENTORY_SITUATION) => {
    if (inventoryToUpdate) {
      await updateExistingInventory(status);
    } else {
      await submitInventory(status);
    }
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          statusOptions={INVENTORY_STATUS_LABELS}
          dashboardHeader="Inventários registrados"
          placeholder="Digite a cidade."
          newItemLabel="Novo inventário"
          onSearch={(data) => refetchInventories(data)}
          entity="inventories"
          tableData={inventoriesData as unknown as Record<string, unknown>[]}
          onNewItemClick={handleNewItemClick}
        />
        <GenericTable
          headers={headers}
          data={
            formatInventoryList(inventoriesData) as unknown as Record<
              string,
              unknown
            >[]
          }
          setFilters={() => {
            setFilters({ ...filters });
          }}
          entity="inventories"
          actions={actions}
        />

        {(isLoading || isConvertingImagesToText) && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={totalItems}
          itemsPerPage={+10}
          goToPage={setPage}
        />
        {isAction("changeSituation") && (
          <ModalChangeStatus
            inventoryId={String((action.data as IInventory)._id)}
          />
        )}
      </div>
      <ImageCapture
        onImageReady={handleImageReady}
        submitInventory={handleSubmitInventory}
        failedImages={failedImages}
      />
    </Suspense>
  );
};
